/**
 * Argus Live — dashboard list (AL-B3).
 *
 * `/argus-live` rail-level landing page. Card grid of the user's
 * dashboards, with a "+ New Dashboard" CTA.
 *
 * Visibility:
 *   - Layer 1: `useHasPermission('read', 'argus_live')` — sidebar nav
 *     entry already filters this, but the page repeats the check
 *     defensively so a direct-URL hit still shows a friendly message
 *     rather than an empty grid.
 *   - Layer 2: the API filters by per-dashboard grants. The card grid
 *     therefore renders only dashboards the user can see.
 *
 * UI:
 *   - Search by title/slug.
 *   - Status filter (all / draft / published / archived).
 *   - Owned / Shared with me filters (cheap derivation off `ownerUserId`).
 *   - Card layout for each dashboard with status pill + last-edited.
 *
 * Empty states are explicit: zero-dashboard tenants see a CTA card; a
 * filter that produces zero results shows an EmptyState with a Clear
 * Filters button.
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  Plus,
  Search,
  Sparkles,
  Loader2,
  LayoutDashboard,
  AlertCircle,
  Share2,
  Pencil,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { StatusPill } from '@/components/ui/status-pill';
import { EmptyState } from '@/components/ui/empty-state';
import { Badge } from '@/components/ui/badge';
import { useHasPermission } from '@/lib/use-has-permission';
import { useArgusLiveDashboards } from '@/lib/use-argus-live';
import { useAuth } from '@/contexts/auth-context';
import { statusToneFor, formatVersionLabel } from '@/lib/argus-live-helpers';
import { cn } from '@/lib/utils';
import { ArgusLiveShareDialog } from '@/components/argus-live';

export const Route = createFileRoute('/argus-live/')({
  component: ArgusLiveListPage,
});

type StatusFilter = 'all' | 'draft' | 'published' | 'archived';
type OwnershipFilter = 'all' | 'mine' | 'shared';

function ArgusLiveListPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const { user } = auth;
  const canRead = useHasPermission('read', 'argus_live');
  const canWrite = useHasPermission('write', 'argus_live');
  const canShare = useHasPermission('share', 'argus_live');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [ownershipFilter, setOwnershipFilter] = useState<OwnershipFilter>('all');
  // The card grid only knows the dashboard's owner (via ownerUserId)
  // — it does NOT have the per-dashboard effectiveRole until the user
  // opens the dashboard detail. For the list-page Share affordance we
  // approximate Layer 2 as "I am the owner", which always grants admin
  // implicitly per the AL-A2 spec. Sysadmins also get the affordance
  // (P2 #15) since they hold a platform-level override. Non-owner
  // non-sysadmin users get the affordance only when they reach the
  // editor (which loads effectiveRole). This avoids an N+1
  // dashboard-fetch when the grid loads.
  const [shareTarget, setShareTarget] = useState<
    { id: string; title: string } | null
  >(null);

  const includeArchived =
    statusFilter === 'archived' || statusFilter === 'all';

  const dashboardsQuery = useArgusLiveDashboards(
    {
      includeArchived,
      ...(statusFilter !== 'all' && statusFilter !== 'archived'
        ? { status: statusFilter }
        : {}),
    },
    { enabled: canRead },
  );

  const filtered = useMemo(() => {
    const rows = dashboardsQuery.data?.data ?? [];
    return rows.filter((d) => {
      // status filter — server already filters except for the
      // 'archived' case (we send includeArchived=true and need to
      // exclude non-archived rows client-side when archived is picked).
      if (statusFilter === 'archived' && d.status !== 'archived') return false;
      if (statusFilter === 'all' && d.status === 'archived') {
        // 'all' means "show every status except archived" by default —
        // matches Tools/Functions semantics. Toggle the explicit
        // 'archived' filter to see archived rows.
        return false;
      }
      if (ownershipFilter === 'mine' && d.ownerUserId !== user.id) return false;
      if (ownershipFilter === 'shared' && d.ownerUserId === user.id) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !d.title.toLowerCase().includes(q) &&
          !d.slug.toLowerCase().includes(q) &&
          !(d.description ?? '').toLowerCase().includes(q)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [
    dashboardsQuery.data,
    statusFilter,
    ownershipFilter,
    search,
    user.id,
  ]);

  if (!canRead) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-6">
        <AlertCircle className="h-10 w-10 text-amber-500/70 mb-3" />
        <h2 className="text-base font-semibold mb-1">
          Argus Live is not enabled for your account
        </h2>
        <p className="text-sm text-muted-foreground max-w-md mb-4">
          You need the <code className="font-mono">read:argus_live</code>{' '}
          permission. Ask a tenant administrator to grant it.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b border-border/40 px-6 py-4 flex items-center justify-between shrink-0 bg-background">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <LayoutDashboard className="h-5 w-5 text-primary" />
            Argus Live
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Chat-driven HTML dashboards that bind to your live operational data.
          </p>
        </div>
        {canWrite && (
          <Button
            size="sm"
            onClick={() => navigate({ to: '/argus-live/dashboards/new' })}
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            New Dashboard
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="border-b border-border/40 px-6 py-3 flex items-center gap-3 shrink-0 bg-card/50">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search dashboards…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pl-8 text-sm"
            aria-label="Search dashboards"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(v) => setStatusFilter(v as StatusFilter)}
        >
          <SelectTrigger
            className="h-8 w-[140px] text-xs"
            aria-label="Filter by status"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={ownershipFilter}
          onValueChange={(v) => setOwnershipFilter(v as OwnershipFilter)}
        >
          <SelectTrigger
            className="h-8 w-[140px] text-xs"
            aria-label="Filter by ownership"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All ownership</SelectItem>
            <SelectItem value="mine">Mine</SelectItem>
            <SelectItem value="shared">Shared with me</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-auto p-6">
        {dashboardsQuery.isLoading ? (
          <div className="flex flex-col items-center justify-center p-12 text-center">
            <Loader2 className="h-6 w-6 text-primary animate-spin mb-3" />
            <p className="text-xs text-muted-foreground">Loading…</p>
          </div>
        ) : dashboardsQuery.error ? (
          <EmptyState
            icon={AlertCircle}
            title="Failed to load dashboards"
            body={dashboardsQuery.error.message}
          />
        ) : filtered.length === 0 ? (
          (dashboardsQuery.data?.data ?? []).length === 0 ? (
            <EmptyState
              icon={Sparkles}
              title="No dashboards yet"
              body={
                canWrite
                  ? 'Build your first Argus Live dashboard by chatting with Argus. We will help you wire it to your live data.'
                  : 'No one has shared a dashboard with you yet. Ask a teammate to share one, or request authoring permissions.'
              }
              action={
                canWrite ? (
                  <Button
                    size="sm"
                    onClick={() => navigate({ to: '/argus-live/dashboards/new' })}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" />
                    Create your first dashboard
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <EmptyState
              icon={Search}
              title="No matching dashboards"
              body="Try clearing the filters or broadening your search."
              action={
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSearch('');
                    setStatusFilter('all');
                    setOwnershipFilter('all');
                  }}
                >
                  Clear filters
                </Button>
              }
            />
          )
        ) : (
          <div
            className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
            data-testid="argus-live-dashboard-grid"
          >
            {filtered.map((d) => (
              <Card
                key={d.id}
                className={cn(
                  'cursor-pointer hover:shadow-md hover:border-primary/40 transition-all',
                  d.status === 'archived' && 'opacity-60',
                )}
                onClick={() =>
                  // A PUBLISHED cockpit opens the OPERATE view (run it). Draft +
                  // archived dashboards have nothing to operate yet, so they go
                  // straight to the editor. An explicit Edit affordance (below)
                  // always reaches the editor for owners/editors.
                  d.status === 'published'
                    ? navigate({
                        to: '/argus-live/dashboards/$id',
                        params: { id: d.id },
                      })
                    : navigate({
                        to: '/argus-live/dashboards/$id/edit',
                        params: { id: d.id },
                      })
                }
                data-testid="argus-live-dashboard-card"
              >
                <CardContent className="p-4 flex flex-col gap-2">
                  <div className="flex items-start justify-between gap-2">
                    <h3
                      className="font-semibold text-sm truncate flex-1"
                      title={d.title}
                    >
                      {d.title}
                    </h3>
                    <div className="flex items-center gap-1">
                      {/*
                       * Explicit Edit affordance. The card click opens the
                       * OPERATE view for a published cockpit; owners/editors
                       * still need a way to reach the EDITOR. Gated on Layer 1
                       * `write:argus_live` + (owner or sysadmin) as a cheap
                       * approximation of "can edit" — the editor route + API
                       * re-check the per-dashboard `edit`/`admin` grant, so a
                       * non-editor who clicks through just lands read-only.
                       */}
                      {canWrite &&
                        (d.ownerUserId === user.id || auth.isSysadmin) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 -my-1"
                            title="Edit dashboard"
                            aria-label="Edit dashboard"
                            data-testid={`argus-live-card-edit-${d.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate({
                                to: '/argus-live/dashboards/$id/edit',
                                params: { id: d.id },
                              });
                            }}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                        )}
                      {/*
                       * S3 P2 #15 fix: the Share button used to be gated on
                       * `canShare && d.ownerUserId === user.id`, which meant
                       * a sysadmin viewing a tenant's Argus Live list saw NO
                       * share affordance — sysadmin is never the implicit
                       * owner of a tenant-scoped dashboard. Per the founder
                       * rule "make every rail load cleanly" we expose the
                       * affordance to sysadmins too; the dialog itself still
                       * enforces Layer 2 via the API's `hasGrant(_, 'admin')`
                       * check on POST/DELETE, so sysadmin presence here
                       * doesn't bypass any authorization — it just makes the
                       * UI discoverable.
                       */}
                      {canShare && (d.ownerUserId === user.id || auth.isSysadmin) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 -my-1"
                          title="Share dashboard"
                          aria-label="Share dashboard"
                          data-testid={`argus-live-card-share-${d.id}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setShareTarget({ id: d.id, title: d.title });
                          }}
                        >
                          <Share2 className="h-3 w-3" />
                        </Button>
                      )}
                      <StatusPill status={statusToneFor(d.status)} compact>
                        {d.status}
                      </StatusPill>
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className="text-[9px] font-mono w-fit"
                  >
                    {d.slug}
                  </Badge>
                  {d.description ? (
                    <p
                      className="text-xs text-muted-foreground line-clamp-2"
                      title={d.description}
                    >
                      {d.description}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground/60 italic">
                      No description
                    </p>
                  )}
                  <div className="flex items-center justify-between mt-1 text-[10px] text-muted-foreground">
                    <span>
                      Updated{' '}
                      {formatVersionLabel({
                        revLabel: '',
                        createdAt: d.updatedAt,
                      })
                        .replace(/^—\s+/, '')
                        .replace(/^ — /, '')}
                    </span>
                    {d.ownerUserId === user.id ? (
                      <span title="You own this dashboard">Owner</span>
                    ) : (
                      <span>Shared</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Share dialog — single instance, target picks the dashboard */}
      {canShare && shareTarget && (
        <ArgusLiveShareDialog
          open={!!shareTarget}
          onOpenChange={(next) => {
            if (!next) setShareTarget(null);
          }}
          dashboardId={shareTarget.id}
          dashboardTitle={shareTarget.title}
          // List view shows the share button to owners (implicit-owner
          // grant gives them admin) and to sysadmins (platform-level
          // override). Both can mutate the grants list; the API still
          // enforces Layer 2 on POST/DELETE so the optimistic UI is
          // safe even if a non-admin somehow reached this branch.
          canMutate={true}
        />
      )}
    </div>
  );
}
